<template>
  <div v-if="!!tab" class="w-screen h-screen pb-safe overflow-hidden flex flex-col">
    <top-bar v-if="!isFastMode" show-home :show-change="false">
      <template #center>
        <div v-if="tab" class="flex items-center font-heading font-bold text-xl">
          <span v-if="tab.code">
            <span class="font-bold">{{ tab.code }}</span>
            <span v-if="tab.tableName"> - {{ tab.tableName }}</span>
            <span v-else-if="tab.name"> - {{ tab.name }}</span>
          </span>
          <span v-else-if="tab.tableName">{{ tab.tableName }}</span>
          <span v-else>{{ tab.name }}</span>

          <l-dropdown
            v-model:open="openTabOptions"
            placement="bottom"
            :mark-selected="false"
            :options="tabOptions"
          >
            <l-button
              v-if="tab.open"
              size="small"
              type="text"
              icon="bracket-down"
              @click="openTabOptions = true"
            />
          </l-dropdown>
        </div>
      </template>
    </top-bar>

    <div class="flex w-full h-full overflow-hidden">
      <menu-browser
        class="flex-1"
        :tab-id="tabId"
        :course-id="selectedCourse"
        :customer-points="availablePoints"
        :customer-used-promotions="customerUsedPromotions"
        @selected-product="configureProduct"
      />
      <open-tab
        v-model:selected-seat="selectedSeat"
        v-model:selected-course="selectedCourse"
        class="flex"
        :tab-id="tabId"
        :catalog-id="catalogId"
        :selected-product-id="selectedProductId"
        @selected-product="editProduct"
        @selected-combo-product="editComboProduct"
      />
    </div>
    <product-editor
      v-if="editingProduct"
      :product="editingProduct"
      :show-course="!selectedCourse"
      show-quantity
      @save-product="saveProduct"
      @close="editingProduct = null"
    />
    <combo-editor
      v-if="editingCombo"
      :editing-combo="editingCombo.edit"
      :catalog-combo="editingCombo.catalog"
      :catalog-id="catalogId"
      :tab-id="tabId"
      :seat="selectedSeat"
      @close="editingCombo = null"
    />
    <link-customer-modal-component
      v-if="linkCustomerModal"
      :tab-id="tabId"
      @close="linkCustomerModal = false"
    />
    <assign-table v-model:is-active="showAssignTablePopup" :tab-ids="[tabId]" />
  </div>
</template>

<script setup lang="ts">
import { storeToRefs } from 'pinia'
import { computed, onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { onBeforeRouteLeave, useRoute, useRouter } from 'vue-router'

import { ProductPriceCalculator } from '@last/core'
import type { LIconName } from '@last/core-ui/paprika'
import { LButton, LDropdown, useDialog } from '@last/core-ui/paprika'
import type { Option } from '@last/core-ui/paprika/components/dropdown/types'

import { getCustomerPromotions } from '@/api/customers'
import TopBar from '@/components/TopBar.vue'
import { useKitchenOrders } from '@/composables/useKitchenOrders'
import { useTabs } from '@/composables/useTabs'
import { useCatalogStore } from '@/store/catalog'
import { useConfigStore } from '@/store/config'
import { useFastMode } from '@/store/fastMode'
import { usePromotionsStore } from '@/store/promotions'
import { useReservationsStore } from '@/store/reservations'
import { useTabsStore } from '@/store/tabs'
import type { CatalogProduct, CatalogProductNormalized, OpenProduct, TabProduct } from '@/types'

import AssignTable from '../tabs/AssignTable.vue'
import ComboEditor from './combos/ComboEditor.vue'
import LinkCustomerModalComponent from './LinkCustomer.vue'
import MenuBrowser from './MenuBrowser.vue'
import ProductEditor from './modifiers/ProductEditor.vue'
import OpenTab from './OpenTab.vue'

const router = useRouter()
const route = useRoute()
const dialog = useDialog()
const { t } = useI18n()

const tabId = computed(() => {
  if (isFastMode.value) {
    return useFastMode().tabId
  } else {
    return route.params.tabId as string
  }
})

const tabsStore = useTabsStore()
const promotionsStore = usePromotionsStore()
const catalogStore = useCatalogStore()
const configStore = useConfigStore()
const reservationStore = useReservationsStore()

const { sortedReservations } = storeToRefs(reservationStore)
const { addProduct, updateProductModifiers, updateCourse } = tabsStore
const { updateCustomerPromotions } = promotionsStore
const { usedPointsInTab } = storeToRefs(promotionsStore)
const { config } = storeToRefs(configStore)
const { tab, catalogId, seatProducts, sharedProducts, closeTab } = useTabs(tabId)
const { sendKitchenOrder, pendingOrders } = useKitchenOrders(tabId)

const showAssignTablePopup = ref(false)

const selectedSeat = ref<number | null>(null)
const selectedCourse = ref<string>('')

const editingProduct = ref<(CatalogProduct & TabProduct) | null>(null)
const editingCombo = ref<{ edit: TabProduct | null; catalog: CatalogProductNormalized } | null>(
  null
)
const editingSeat = ref<number | null>(null)

const customerPoints = ref(0)
const customerUsedPromotions = ref<string[]>([])
const linkCustomerModal = ref(false)

const openTabOptions = ref(false)

const isFastMode = computed(() => {
  return route.name === 'fastMode'
})

const tabOptions = computed<Option[]>(() => {
  const options = [
    {
      icon: 'user' as LIconName,
      label: tab.value.customerInfo?.name ?? t('ordering.link-customer'),
      value: 'link-customer',
      onClick: () => {
        linkCustomerModal.value = true
        openTabOptions.value = false
      }
    },
    {
      icon: (tab.value.tables.length > 0 ? 'double-arrow-sides' : 'plus') as LIconName,
      label: tab.value.tables.length > 0 ? t('floorplan.move-account') : t('tabs.assign-to-table'),
      value: 'assign-to-table',
      onClick: () => {
        showAssignTablePopup.value = true
        openTabOptions.value = false
      }
    },

    {
      icon: 'close' as LIconName,
      label: t('close-tab.close-tab'),
      value: 'close',
      onClick: () => {
        closeTab()
        openTabOptions.value = false
      }
    }
  ]

  const hasReservation = sortedReservations.value.some(
    reservation => reservation.tabId === tabId.value
  )
  if (!hasReservation) {
    options.splice(2, 0, {
      icon: 'calendar' as LIconName,
      label: t('ordering.link-reservation'),
      value: 'link-reservation',
      onClick: () => {
        router.push({ name: 'linkReservations', query: { tabId: tabId.value } })
        openTabOptions.value = false
      }
    })
  }

  return options
})

const selectedProductId = computed(() => (editingProduct.value ? editingProduct.value.id : null))

const productCount = computed(() => seatProducts.value.flat().length + sharedProducts.value.length)

const tabCustomerId = computed(() => tab.value?.customerId)

const availablePoints = computed(() => customerPoints.value - usedPointsInTab.value(tabId.value))

const tableHasFlatFeeSurcharge = computed(() => {
  return tab.value.flatFeeSurcharge || 0
})

watch(productCount, (oldCount, newCount) => {
  if (oldCount != newCount) {
    editingProduct.value = null
  }
})

watch(tabCustomerId, (oldId, newId) => {
  if (oldId != newId) {
    updatePromotionsInfo()
  }
})

onMounted(() => {
  updatePromotionsInfo()
})

onBeforeRouteLeave((to, _, next) => {
  if (
    isFastMode.value &&
    to.name !== 'checkout' &&
    to.name !== 'checkout2' &&
    sharedProducts.value.length > 0
  ) {
    dialog({
      title: t('ordering.exit-fastpos-title'),
      content: t('ordering.exit-fastpos-description'),
      mainLabel: t('ordering.exit-fastpos-stay'),
      secondaryLabel: t('ordering.exit-fastpos-exit'),
      onSecondary: () => {
        useFastMode().closeTab()
        next()
      }
    })
  } else if (
    pendingOrders.value &&
    tab.value.activationTime &&
    config.value.enableKitchenOrders &&
    tab.value.open &&
    to.matched[0].name === 'pos'
  ) {
    dialog({
      title: t('unsent-dialog.title'),
      content: t('unsent-dialog.message'),
      mainLabel: t('unsent-dialog.confirm'),
      secondaryLabel: t('unsent-dialog.cancel'),
      onConfirm: async () => {
        sendKitchenOrder('all')
        next()
      },
      onSecondary: () => {
        next()
      }
    })
  } else {
    next()
  }
})

async function updatePromotionsInfo() {
  let points = 0
  const customerId = tab.value?.customerId
  if (customerId) {
    const response = await getCustomerPromotions(customerId)
    points = response.points
    updateCustomerPromotions({
      customerId,
      customerPromotions: response.customerPromotions
    })
    customerUsedPromotions.value = response.customerUsedPromotions
  }
  customerPoints.value = points
}

function saveProduct(product: any) {
  const productPricing = ProductPriceCalculator.calculateProductPricing(product)
  if (product.id) {
    updateProductModifiers({
      productId: product.id,
      modifiers: product.modifiers,
      comments: product.comments,
      productPricing
    })
    updateCourse({
      productId: product.id,
      course: product.course
    })
  } else {
    addProduct({
      tabId: tabId.value,
      seat: selectedSeat.value,
      product: {
        ...product,
        ...productPricing
      }
    })
  }
  editingProduct.value = null
}

function configureProduct(product: CatalogProduct | OpenProduct, quantity = 1) {
  const parentProduct = 'id' in product ? catalogStore.getProductById(product.id) : {}
  if ('categories' in product && product.categories) {
    editingCombo.value = {
      catalog: {
        ...product,
        ...(parentProduct as CatalogProductNormalized),
        price: product.price!
      },
      edit: null
    }
  } else {
    const newProduct = {
      ...product,
      ...(parentProduct as CatalogProductNormalized),
      price: product.price,
      course: selectedCourse.value || product.course,
      id: null,
      parentProduct: 'id' in product ? product.id : undefined,
      quantity,
      flatFeeSurcharge: tableHasFlatFeeSurcharge.value
    }

    const hasMandatoryModifiers =
      'modifierGroups' in newProduct &&
      !!(newProduct.modifierGroups as CatalogProductNormalized['modifierGroups'])?.some(
        group => group.min > 0
      )
    if (hasMandatoryModifiers) {
      editProduct(newProduct)
    } else {
      saveProduct({ ...newProduct, modifiers: [] })
    }
  }
}

function editComboProduct({ product }: { product: TabProduct }) {
  const parentProduct = catalogStore.getProductById(product.parentProduct!)
  if (parentProduct && parentProduct.categories) {
    editingCombo.value = {
      catalog: parentProduct,
      edit: product
    }
  }
}

function editProduct(product: any) {
  if (!product) {
    editingProduct.value = null
    return
  }
  editingSeat.value = selectedSeat.value
  const parentProduct = catalogStore.getProductById(product.parentProduct)
  if (parentProduct && parentProduct.categories) {
    editingCombo.value = {
      catalog: parentProduct,
      edit: product
    }
  } else {
    editingProduct.value = {
      ...parentProduct,
      quantity: product.quantity,
      modifiers: product.modifiers,
      comments: product.comments,
      id: product.id,
      parentProduct: (parentProduct && parentProduct.id) || null,
      course: product.course,
      price: product.price,
      name: product.name,
      discountType: product.discountType,
      discountAmount: product.discountAmount,
      pointsExpense: product.pointsExpense
    }
  }
}
</script>
